<template>
  <header class="ss__custom__header__wrapper" :active="!!menuItemHovered || searchVisible || mobileMenuVisible">
    <div class="ss__custom__header">
      <div class="ss__custom__header__left ss__custom__header__logo__wrapper">
        <a href="/" alt="首页" class="ss__custom__header__logo__link">
          <SsCustomHeaderLogo class="ss__custom__header__logo--desktop"/>
          <SsCustomHeaderLogo class="ss__custom__header__logo--mobile" viewBox="0 0 36 36"/>
        </a>
      </div>
      <div class="ss__custom__header__desktop__menu__wrapper">
        <SsCustomHeaderDesktopMenu
          class="ss__custom__header__menu"
          :menu="customMenuData"
          :menuItemHovered.sync="menuItemHovered"
          @show="showSubmenu"
          @hide="hideSubmenu"
        />
      </div>
      <div class="ss__custom__header__right">
        <SsCustomHeaderMobileMenu
          class="ss__custom__header__handle ss__custom__header__handle--mobile"
          :menu="customMenuDataMobile"
          :visible.sync="mobileMenuVisible"
        />
        <SsCustomHeaderSearch
          class="ss__custom__header__handle ss__custom__header__handle--desktop"
          :visible.sync="searchVisible"
          :searchUrl="searchUrl"
        />
        <SsCustomHeaderWrite
          class="ss__custom__header__handle ss__custom__header__handle--desktop"
          :isLogin="isLogin"
          @login="$emit('login')"
        />
        <SsCustomHeaderNotice
          v-if="isLogin"
          class="ss__custom__header__handle"
          :userInfo="userInfo"
          :noticeCount="noticeCount"
          :visible.sync="noticeVisible"
        />
        <SsCustomHeaderUser
          class="ss__custom__header__handle comp__SsCustomHeaderUser"
          :visible.sync="userMenuVisible"
          :isLogin="isLogin"
          :userInfo="userInfo"
          :reminders="reminders"
          @login="$emit('login')"
          @logout="$emit('logout')"
        />
      </div>
    </div>

    <SsDesktopSubmenu
      :menu="customMenuData"
      :menuItemHovered.sync="menuItemHovered"
      @show="showSubmenu"
      @hide="hideSubmenu"
    />

    <SsDialog2
      :visible.sync="mobileMenuVisible"
      :btnCloseVisible="false"
      :btnSubmitVisible="false"
      class="ss__custom__header__mobile__menu"
      @click.native.stop>
      <template v-slot:dialog>
        <SsMobileMenu
          class="comp__SsMobileMenu"
          @close="toggleMobileMenuVisible"
          :menu="customMenuDataMobile"
          :searchUrl="searchUrl"
        />
      </template>
    </SsDialog2>
  </header>
</template>

<script>
import SsCustomHeaderSearch from './_components/SsCustomHeaderSearch/index.vue'
import SsCustomHeaderWrite from './_components/SsCustomHeaderWrite/index.vue'
import SsCustomHeaderMobileMenu from './_components/SsCustomHeaderMobileMenu/index.vue'
import SsCustomHeaderDesktopMenu from './_components/SsDesktopMenu/index.vue'
import SsDesktopSubmenu from './_components/SsDesktopSubmenu/index.vue'
import SsMobileMenu from '../SsMobileMenu/index.vue'
import SsDialog2 from '../ss-dialog2'
import SsCustomHeaderLogo from '../SsCustomHeaderLogo'
import SsCustomHeaderUser from '../SsCustomHeaderUser'
import SsCustomHeaderNotice from '../SsCustomHeaderNotice'
import now from 'lodash/now'

function looseJsonParse (obj) {
  // eslint-disable-next-line no-new-func
  return Function('"use strict";return (' + obj + ')')()
}

export default {
  name: 'SsCustomHeader',

  components: {
    SsCustomHeaderSearch,
    SsCustomHeaderWrite,
    SsCustomHeaderMobileMenu,
    SsCustomHeaderDesktopMenu,
    SsDesktopSubmenu,
    SsMobileMenu,
    SsDialog2,
    SsCustomHeaderLogo,
    SsCustomHeaderUser,
    SsCustomHeaderNotice
  },

  props: {
    isLogin: {
      type: Boolean,
      default: false
    },
    userInfo: {
      type: Object,
      default: null
    },
    whitelist: {
      type: Array,
      default: () => ([])
    },
    searchUrl: {
      type: String,
      default: '/search?q=[keyword]'
    },
    cacheTtl: {
      type: Number,
      default: 60000
    },
    initial: {
      type: Array,
      default: null
    },
    noticeCount: {
      type: Object
    }
  },

  provide () {
    return {
      userInfo: this.userInfo,
      whitelist: this.whitelist,
      $getUserInfo: () => {
        return this.userInfo
      }
    }
  },

  data () {
    return {
      customMenuData: null,
      customMenuDataMobile: null,
      customMenuOriginalData: null,
      noticeVisible: false,
      userMenuVisible: false,
      searchVisible: false,
      mobileMenuVisible: false,
      menuItemHovered: '',
      hoverTimer: null
    }
  },

  computed: {
    isPrime () {
      if (!this.userInfo) return false
      return this.userInfo.member && this.userInfo.member.is_pay && !this.userInfo.member.is_expire
    },
    reminders () {
      return {
        series: Boolean(this.userInfo && this.userInfo.no_read), // 栏目未读
        series_comment: Boolean(this.noticeCount && this.noticeCount.series_comment_count) // 栏目评论未处理
      }
    }
  },

  watch: {
    userInfo () {
      if (this.customMenuOriginalData) {
        this.format()
      }
    },
    initial: {
      immediate: true,
      handler: function () {
        if (!this.customMenuOriginalData && this.initial) {
          this.customMenuOriginalData = this.initial
          this.format()
        }
      }
    }
  },

  methods: {
    async fetchHeaderData () {
      try {
        const res = await this.$http({
          url: '/menu/page/get'
        })
        this.customMenuOriginalData = res.data
        this.format()
        window.localStorage.setItem('ssCustomHeaderData', JSON.stringify({
          data: this.customMenuOriginalData,
          expiry: now() + this.cacheTtl
        }))
      } catch (error) {
        this.$toast({ type: 'error', message: error.message })
      }
    },
    format () {
      this.customMenuData = this.formatHeaderData(this.customMenuOriginalData)
      this.customMenuDataMobile = this.formatHeaderData(this.customMenuOriginalData, true)
    },
    formatHeaderData (input, isMobile) {
      const output = input.map(menuPrimary => {
        let { subs: menuSecondary } = menuPrimary
        if (menuPrimary.type === 2 && menuPrimary.code) {
          menuSecondary = looseJsonParse(menuPrimary.code)
          menuSecondary = menuSecondary.filter(item => {
            if (isMobile) {
              return item.mobile
            }
            if (item.mobile) return false
            const { auth } = item
            if (!auth) return true
            // 实际值是否与期望一致
            if (auth.isPrime) {
              return auth.isPrime.expected === Boolean(this.isPrime)
            }
            if (auth.isLogin) {
              return auth.isLogin.expected === Boolean(this.isLogin)
            }
            return false
          })
        } else {
          menuSecondary = menuSecondary.map(menuTertiary => {
            let { sub_details: children } = menuTertiary
            children = children.map(item => {
              return {
                ...this.getCardType(item),
                id: item.id,
                icon: item.iconfont,
                image: item.icon,
                name: item.title,
                style: item.style,
                url: item.url,
                description: item.description,
                blank: item.blank,
                external: !this.$testLink(item.url, ['sspai.com'])
              }
            })
            return {
              id: menuTertiary.id,
              icon: menuTertiary.iconfont,
              name: menuTertiary.title,
              style: menuTertiary.style,
              layout: menuTertiary.style_type === 1 ? 'card' : 'normal',
              url: menuPrimary.url,
              blank: menuPrimary.blank,
              children
            }
          })
        }
        return {
          id: menuPrimary.id,
          icon: menuPrimary.iconfont,
          name: menuPrimary.title,
          style: menuPrimary.style,
          children: menuSecondary,
          code: (menuPrimary.type === 2 && menuPrimary.code) || '',
          url: menuPrimary.url,
          blank: menuPrimary.blank
        }
      })
      // console.log(output)
      return output
    },
    getCardType (item) {
      switch (item.object_type) {
        case 2:
          return {
            type: '栏目',
            card: 'SsDesktopMenuSeriesItem'
          }
        case 3:
          return {
            type: '软件',
            card: 'SsDesktopMenuGoodItem',
            price: item.price,
            originPrice: item.origin_price,
            memberPrice: item.member_price
          }
        case 4:
          return {
            type: '实体',
            card: 'SsDesktopMenuGoodItem',
            price: item.price,
            originPrice: item.origin_price,
            memberPrice: item.member_price
          }
        case 5:
          return {
            type: '文章',
            card: 'SsDesktopMenuPostItem'
          }
        case 6:
          return {
            type: '一派',
            card: 'SsDesktopMenuPostItem'
          }
        case 7:
          return {
            type: '共创',
            card: item.id < 0 ? 'SsCoCreationEntryCard' : 'SsCoCreationItemCard',
            initiator: item.co_creation?.initiator_user,
            participationCount: item.co_creation?.participation_count,
            updates: item.co_creation?.current_fixed_show_progress
          }
        default:
          return {
            type: '',
            card: 'SsDesktopMenuItem'
          }
      }
    },
    showSubmenu (name) {
      if (this.hoverTimer) {
        clearTimeout(this.hoverTimer)
        this.hoverTimer = null
      }
      this.menuItemHovered = name
    },
    hideSubmenu (e) {
      this.hoverTimer = setTimeout(() => {
        this.menuItemHovered = ''
        clearTimeout(this.hoverTimer)
        this.hoverTimer = null
      }, 50)
    },
    toggleMobileMenuVisible () {
      this.mobileMenuVisible = !this.mobileMenuVisible
    },
    handleSsCustomHeaderToggleTarget (e) {
      const { detail } = e
      if (detail !== 'search') {
        this.searchVisible = false
      }
      if (detail !== 'notice') {
        this.noticeVisible = false
      }
      if (detail !== 'menu') {
        this.mobileMenuVisible = false
      }
      if (detail !== 'user') {
        this.userMenuVisible = false
      }
    }
  },

  created () {
    let cache = null
    let expiry = 0
    if (!this.$isServer) {
      cache = window.localStorage.getItem('ssCustomHeaderData')
      try {
        cache = JSON.parse(cache)
        expiry = cache.expiry
        this.customMenuOriginalData = cache.data
        this.format()
      } catch (error) {
        window.localStorage.removeItem('ssCustomHeaderData', '')
      }
    }
    if (expiry < now()) {
      this.fetchHeaderData()
    }
  },

  mounted () {
    window.addEventListener('ssCustomHeaderToggleTarget', this.handleSsCustomHeaderToggleTarget)
  },

  beforeDestroy () {
    window.removeEventListener('ssCustomHeaderToggleTarget', this.handleSsCustomHeaderToggleTarget)
  }
}
</script>

<style lang="stylus" scoped>
@import '~@/assets/styles/media.styl'

.ss__custom__header {
  position: relative;
  z-index: 3;
  display: flex;
  justify-content space-between;
  align-items: center;
  max-width: 1200px;
  padding: 0px 16px;
  margin: auto;

  .ss__custom__header__logo__wrapper {
    padding: 12px 0px;
    .ss__custom__header__logo__link {
      display: flex;
      align-items: center;
    }
    .ss__custom__header__logo--mobile {
      width: var(--size-w-ssCustomHeaderLogo, 32px);
      height: var(--size-h-ssCustomHeaderLogo, 32px);
    }
    .ss__custom__header__logo--desktop {
      display: none;
    }
    @media (min-width: $screen-lg) {
      padding: 10px 0px;
      .ss__custom__header__logo--mobile {
        display: none;
      }
      .ss__custom__header__logo--desktop {
        display: block;
        width: var(--size-w-ssCustomHeaderLogo--desktop, auto);
        height: var(--size-h-ssCustomHeaderLogo--desktop, 36px);
      }
    }
  }

  .ss__custom__header__left {
    position: relative;
    z-index: 1;
  }

  .ss__custom__header__right {
    display: flex;
    align-items: center;
    column-gap: 8px;
    padding: 12px 0px;
    .ss__custom__header__handle--desktop {
      display: none;
      @media (min-width: $screen-lg) {
        display: block;
      }
    }
    .ss__custom__header__handle--mobile {
      @media (min-width: $screen-lg) {
        display: none;
      }
    }
    .comp__SsCustomHeaderUser {
      >>> .ss__custom__header__user__avatar {
        margin-left: 2px;
        @media (min-width: $screen-lg) {
          margin-left: 6px;
        }
      }
    }
  }

  .ss__custom__header__desktop__menu__wrapper {
    display: none;
    @media (min-width: $screen-lg) {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  }

  >>> .ss__custom__header__fade-enter-active,
  >>>.ss__custom__header__fade-leave-active {
    transition opacity .3s var(--a-func-default);
  }
  >>> .ss__custom__header__fade-enter,
  >>> .ss__custom__header__fade-leave-to {
    opacity: 0;
  }
}

.ss__custom__header__wrapper::after {
  content: '';
  display: block;
  background: var(--color-ssCustomHeader-bg, var(--B0-t));
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  z-index: 2;
}

.ss__custom__header__mobile__menu {
  &.ss__dialog2__wrapper {
    >>> .ss__dialog2__mask {
      background: transparent;
    }
    >>> .ss__dialog2--blank {
      height: calc(var(--vh, 1vh) * 100 - var(--h-ssCustomHeader));
      top: var(--h-ssCustomHeader);
      background: var(--B0-t);
    }
    >>> .body-enter-active,
    >>> .body-leave-active {
      opacity: 1
      transform: translateY(0px);
      transition: all  0.6s var(--a-func-default);
    }

    >>> .body-enter,
    >>> .body-leave-to {
      opacity: 0
      transform: translateY(-20px);
    }
  }
  @media (min-width: $screen-lg) {
    display: none;
  }
}
</style>
